/**
 * Agent session list page — `GET /sessions`.
 *
 * Migrated onto the CoOrdio list-page pattern (see `ProjectListPage.tsx`, the reference):
 * `ShellHeader` + `CordioAbpPagedDataTable` + one `DeleteDialog`, with the grid owning paging,
 * search and filtering. The page's `pageNumber` / `searchTerm` / `statusFilter` state and the
 * `useEffect` that refetched on every change are gone, and so is its dependence on
 * `sessionStore.sessions` — the grid holds the rows now. `useSessionStore` is still the home of the
 * *mutations* (create / pause / resume / delete) so `currentSession` stays in step with them for
 * `SessionDetailPage`; only the list read moved to `sessionService`.
 *
 * ─── Column plan ────────────────────────────────────────────────────────────────────────────────
 * field           │ header        │ width │ cell type          │ filter
 * ────────────────┼───────────────┼───────┼────────────────────┼──────────────────────────────────
 * sessionName     │ Session       │  24%  │ text (clickable)   │ toolbar search (`searchTerm`)
 * agentName       │ Agent         │  18%  │ text               │ popover select → `agentDefinitionId`
 * status          │ Status        │  13%  │ custom Chip        │ status tabs → synthetic `statusTab`
 * totalTokensUsed │ Tokens        │  13%  │ custom (locale nº) │ —
 * totalCost       │ Cost          │  12%  │ custom (USD)       │ —
 * lastActivityAt  │ Last Activity │  20%  │ date (locale)      │ popover date → `startDate`/`endDate`
 *
 * Dropped from the old 8-column table: `messageCount` (tokens is the load metric that matters and
 * message count is on the detail page), `createdAt` as a column of its own (it is the fallback
 * value behind Last Activity instead), and the unlabelled compaction icon column — that one became
 * a conditional row action rather than a column, so the capability survives without a 48px column
 * carrying no header.
 * ────────────────────────────────────────────────────────────────────────────────────────────────
 *
 * No column is `sortable`: `sessionService.getSessions` builds its query string from an explicit
 * allow-list and has nowhere to put `sortBy` / `sortDescending`, so a sort header would silently
 * do nothing.
 */

import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  Button,
  CordioAbpPagedDataTable,
  Chip,
  ShellHeader,
  type ChipColor,
  type CordioDataTableAction,
  type CordioDataTableColumn,
  type CordioDataTableListToolbarConfig,
} from '@coordio/ui';
import { Plus, Trash2 } from 'lucide-react';

import { DeleteDialog, useDeleteDialog } from '../components/delete-dialog';
import { createCortexPagedDataSource } from '../components/data-display/cortexPagedDataSource';
import { CreateSessionDialog } from '../components/sessions/CreateSessionDialog';
import { agentService } from '../services/agentService';
import { sessionService } from '../services/sessionService';
import { useContextStore } from '../store/contextStore';
import { useNotificationStore } from '../store/notificationStore';
import { useSessionStore } from '../store/sessionStore';
import { SessionStatus } from '../types/session';
import { ROUTES } from '../utils/routes';

import type { AgentSessionDto, CreateSessionRequest } from '../types/session';

import './SessionListPage.scss';

/**
 * Quick-filter tabs over the session lifecycle, carrying exactly the options the old inline Status
 * dropdown offered. The All tab declares the literal `'all'` because the grid builds each tab's
 * test id as `cordio-datatable-status-tab-<value>`; an empty value emits
 * `cordio-datatable-status-tab-` and GRID-13's `…-status-tab-all` selector matches nothing.
 *
 * Expired and Error are deliberately absent from the tabs, as they were from the dropdown: they are
 * terminal states nobody browses by. Their rows still appear under All with their own chip.
 */
const STATUS_TAB_OPTIONS = [
  { label: 'All', value: 'all' },
  { label: 'Active', value: String(SessionStatus.Active) },
  { label: 'Paused', value: String(SessionStatus.Paused) },
  { label: 'Completed', value: String(SessionStatus.Completed) },
  { label: 'Cancelled', value: String(SessionStatus.Cancelled) },
] as const;

/**
 * Synthetic toolbar key the status tabs write to.
 *
 * It cannot be `status` — that is the real API parameter, and the tabs also carry the value `'all'`,
 * which the endpoint has no meaning for. {@link toSessionQuery} translates the tab into a numeric
 * `status` and drops it entirely for All.
 */
const STATUS_TAB_PARAM_KEY = 'statusTab';

/** Turns the grid's query into the one `sessionService.getSessions` accepts. */
function toSessionQuery(query: Record<string, unknown>): Record<string, unknown> {
  const { [STATUS_TAB_PARAM_KEY]: statusTab, ...rest } = query;

  if (typeof statusTab === 'string' && statusTab !== 'all' && statusTab !== '') {
    const parsed = Number.parseInt(statusTab, 10);
    if (!Number.isNaN(parsed)) {
      return { ...rest, status: parsed };
    }
  }

  return rest;
}

/** The status vocabulary the rest of the product uses, so the chip matches the tabs above it. */
const STATUS_LABELS: Record<number, string> = {
  [SessionStatus.Active]: 'Active',
  [SessionStatus.Paused]: 'Paused',
  [SessionStatus.Completed]: 'Completed',
  [SessionStatus.Cancelled]: 'Cancelled',
  [SessionStatus.Expired]: 'Expired',
  [SessionStatus.Error]: 'Error',
};

const STATUS_COLORS: Record<number, ChipColor> = {
  [SessionStatus.Active]: 'green',
  [SessionStatus.Paused]: 'amber',
  [SessionStatus.Completed]: 'blue',
  [SessionStatus.Cancelled]: 'default',
  [SessionStatus.Expired]: 'default',
  [SessionStatus.Error]: 'red',
};

/** True when the session's chat history has been compacted into context summaries. */
const isCompacted = (row: AgentSessionDto) =>
  row.hasContextSummaries ?? (row.contextSummaryCount ?? 0) > 0;

export const SessionListPage = () => {
  const navigate = useNavigate();

  const currentTenant = useContextStore((s) => s.currentTenant);
  const currentProject = useContextStore((s) => s.currentProject);
  const { showNotification } = useNotificationStore();

  // Selector-scoped rather than destructured off the whole store: this page no longer reads the
  // store's session list, so it must not re-render when some other screen mutates it.
  const createSession = useSessionStore((s) => s.createSession);
  const deleteSession = useSessionStore((s) => s.deleteSession);
  const pauseSession = useSessionStore((s) => s.pauseSession);
  const resumeSession = useSessionStore((s) => s.resumeSession);

  /** Bumped after a mutation to rebuild `dataSource` and make the grid refetch. */
  const [listVersion, setListVersion] = useState(0);
  const [createOpen, setCreateOpen] = useState(false);

  const { dialogRef, pendingItem, openDelete, closeDelete } = useDeleteDialog<AgentSessionDto>();

  const tenantId = currentTenant?.id;
  const projectId = currentProject?.id;

  const dataSource = useMemo(
    () =>
      createCortexPagedDataSource<AgentSessionDto>((query) => sessionService.getSessions(toSessionQuery(query))),
    // `tenantId` / `projectId` scope the request through the API client's headers rather than the
    // query string, so they still have to invalidate the list. `listVersion` is the deliberate
    // refetch lever: changing the identity of `dataSource` is the documented way to reload.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tenantId, projectId, listVersion],
  );

  const columns = useMemo<ReadonlyArray<CordioDataTableColumn<AgentSessionDto>>>(
    () => [
      { field: 'sessionName', header: 'Session', type: 'text', width: '24%', clickable: true },
      { field: 'agentName', header: 'Agent', type: 'text', width: '18%' },
      {
        field: 'status',
        header: 'Status',
        type: 'custom',
        width: '13%',
        customCell: (row) => (
          <Chip label={STATUS_LABELS[row.status] ?? 'Unknown'} color={STATUS_COLORS[row.status] ?? 'default'} />
        ),
      },
      {
        field: 'totalTokensUsed',
        header: 'Tokens',
        type: 'custom',
        width: '13%',
        // A custom cell rather than `type: 'number'`: the built-in number cell prints the raw
        // integer, and six-figure token counts are unreadable without thousands separators.
        customCell: (row) => <>{(row.totalTokensUsed ?? 0).toLocaleString()}</>,
      },
      {
        field: 'totalCost',
        header: 'Cost',
        type: 'custom',
        width: '12%',
        // Four decimals: per-session spend is routinely a fraction of a cent.
        customCell: (row) => <>{`$${(row.totalCost ?? 0).toFixed(4)}`}</>,
      },
      {
        field: 'lastActivityAt',
        header: 'Last Activity',
        type: 'date',
        dateFormat: 'locale',
        width: '20%',
        // A session that has never been touched since creation has no `lastActivityAt`; showing
        // "—" there would read as "never used" rather than "not used yet".
        valueGetter: (row) => row.lastActivityAt ?? row.createdAt,
      },
    ],
    [],
  );

  const listToolbar = useMemo<CordioDataTableListToolbarConfig>(
    () => ({
      search: { placeholder: 'Search sessions by name' },
      statusTabs: {
        paramKey: STATUS_TAB_PARAM_KEY,
        ariaLabel: 'Session status filter',
        options: [...STATUS_TAB_OPTIONS],
      },
      filters: [
        {
          key: 'agentDefinitionId',
          label: 'Agent',
          type: 'select',
          // Lazy: runs the first time the popover opens, never on page load. The endpoint takes an
          // agent id, so the options have to be resolved from the agent registry.
          loadOptions: async () => {
            const page = await agentService.getAgents({ pageNumber: 1, pageSize: 200 });
            return page.items.map((agent) => ({ label: agent.name, value: agent.id }));
          },
        },
        { key: 'startDate', label: 'Active from', type: 'date' },
        { key: 'endDate', label: 'Active until', type: 'date' },
      ],
      filterButtonLabel: 'Filter',
    }),
    [],
  );

  const handleCreate = useCallback(
    async (data: CreateSessionRequest) => {
      try {
        await createSession(data);
        showNotification('Session created successfully', 'success');
        setCreateOpen(false);
        setListVersion((v) => v + 1);
      } catch (err) {
        showNotification(err instanceof Error ? err.message : 'Failed to create session', 'error');
      }
    },
    [createSession, showNotification],
  );

  const handlePause = useCallback(
    async (row: AgentSessionDto) => {
      try {
        await pauseSession(row.id);
        showNotification('Session paused', 'success');
        setListVersion((v) => v + 1);
      } catch (err) {
        showNotification(err instanceof Error ? err.message : 'Failed to pause session', 'error');
      }
    },
    [pauseSession, showNotification],
  );

  const handleResume = useCallback(
    async (row: AgentSessionDto) => {
      try {
        await resumeSession(row.id);
        showNotification('Session resumed', 'success');
        setListVersion((v) => v + 1);
      } catch (err) {
        showNotification(err instanceof Error ? err.message : 'Failed to resume session', 'error');
      }
    },
    [resumeSession, showNotification],
  );

  const actions = useMemo<ReadonlyArray<CordioDataTableAction<AgentSessionDto>>>(
    () => [
      {
        icon: 'eye',
        tooltip: 'View session',
        onClick: (row) => navigate(ROUTES.SESSION_DETAIL.replace(':id', row.id)),
      },
      {
        icon: 'fold-vertical',
        tooltip: 'Chat history compacted — view summaries',
        // Was an unlabelled 48px column; as a conditional action it costs no table width and still
        // deep-links into the summaries section of the detail page.
        visible: isCompacted,
        onClick: (row) => navigate(`${ROUTES.SESSION_DETAIL.replace(':id', row.id)}#context-summaries`),
      },
      {
        icon: 'pause',
        tooltip: 'Pause session',
        visible: (row) => row.status === SessionStatus.Active,
        onClick: (row) => void handlePause(row),
      },
      {
        icon: 'play',
        tooltip: 'Resume session',
        visible: (row) => row.status === SessionStatus.Paused,
        onClick: (row) => void handleResume(row),
      },
      {
        icon: 'trash-2',
        tooltip: 'Delete session',
        className: 'session-list__action--destructive',
        onClick: (row) => openDelete(row),
      },
    ],
    [navigate, handlePause, handleResume, openDelete],
  );

  const handleDeleteConfirm = useCallback(async () => {
    if (!pendingItem) return;

    await deleteSession(pendingItem.id);
    showNotification('Session deleted successfully', 'success');
    setListVersion((v) => v + 1);
  }, [pendingItem, deleteSession, showNotification]);

  return (
    <div className="session-list" data-testid="session-list-page">
      <ShellHeader
        title="Agent Sessions"
        description="Every conversation an agent has held — its status, its token spend and where it left off."
        data-testid="session-list-header"
        actions={
          <Button type="button" variant="default" size="sm" className="gap-2" data-testid="add-session-btn" onClick={() => setCreateOpen(true)}>
            <Plus className="h-4 w-4 shrink-0" aria-hidden />
            New Session
          </Button>
        }
      />

      <div data-testid="session-list-grid">
        <CordioAbpPagedDataTable<AgentSessionDto>
          className="session-list__table"
          columns={columns}
          dataSource={dataSource}
          initialPageSize={25}
          maxBodyHeight="520px"
          listToolbar={listToolbar}
          actions={actions}
          onRowClick={(row) => navigate(ROUTES.SESSION_DETAIL.replace(':id', row.id))}
        />
      </div>

      <DeleteDialog
        ref={dialogRef}
        title="Delete session?"
        entityLabel={pendingItem?.sessionName}
        dialogIcon={<Trash2 className="delete-dialog__icon" />}
        description="The whole conversation goes with it — every message, tool call and context summary, plus the token and cost record for this run. This cannot be undone."
        confirmLabel="Delete"
        confirmVariant="destructive"
        busyLabel="Deleting…"
        onConfirm={handleDeleteConfirm}
        onCancel={closeDelete}
      />

      <CreateSessionDialog open={createOpen} onClose={() => setCreateOpen(false)} onSubmit={handleCreate} />
    </div>
  );
};
